/**
 * „Verfügbarkeit & Vertrieb" — Grundkalender, Monat je Apartment und Kanalregeln einer Objektgruppe.
 *
 * Datum, Länge, Reiter, Apartment und Monat sind URL-State; das Objekt kommt aus der globalen
 * Objektwahl. Ohne `Edit` werden Mutation-Controls nicht gerendert, nicht deaktiviert; die API
 * bleibt die Sicherheitsgrenze. Solange eine Übertragung offen ist, fragt die Seite alle 2 s nach.
 */
import { useState } from 'react';
import { EmptyState } from '@/shared/components';
import { AvailabilityCalendarCard } from './AvailabilityCalendarCard';
import { AutomationLayerProvider } from './automation/AutomationLayerContext';
import { useAutomationLayerQuery } from './automation/useAutomationLayerQuery';
import { AvailabilityHeader } from './AvailabilityHeader';
import { AvailabilityMonth, type MonthHandover } from './AvailabilityMonth';
import { ChannelCloseoutPanel } from './ChannelCloseoutPanel';
import type { IsoRange } from './calendar/rangePickerModel';
import { monthOf } from './month/monthModel';
import { useAvailabilityPage } from './useAvailabilityPage';

export function AvailabilityDistributionPage() {
  const page = useAvailabilityPage();
  const { group, calendar, updateSearch } = page;
  const [handover, setHandover] = useState<MonthHandover | null>(null);
  const automation = useAutomationLayerQuery(group?.id, page.windowFrom);
  // Grundkalender, Kanalregeln und „Werte setzen“ führen in den Monat – mit der Auswahl dorthin.
  const openMonth = (apartmentId: string, ranges: IsoRange[] = []) => {
    setHandover(ranges.length > 0 ? { apartmentId, ranges } : null);
    updateSearch({
      tab: undefined,
      apartment: apartmentId,
      month: monthOf(ranges[0]?.from ?? page.from),
      day: undefined,
    });
  };

  if (page.apartmentId && group) {
    return (
      <AutomationLayerProvider layer={automation.layer} status={automation.status}>
        <AvailabilityMonth page={page} handover={handover} />
      </AutomationLayerProvider>
    );
  }
  return (
    <AutomationLayerProvider layer={automation.layer} status={automation.status}>
      <div className="min-w-0">
        <AvailabilityHeader
          tab={page.tab}
          onTabChange={(next) => updateSearch({ tab: next === 'channels' ? next : undefined })}
          summary={page.summary}
          from={page.from}
          to={page.to}
          days={page.days}
          onFromChange={(iso) => updateSearch({ from: iso })}
          onDaysChange={(next) => updateSearch({ days: next })}
          onToday={() => updateSearch({ from: page.today })}
        />
        <div className="relative z-10 -mt-[74px] flex flex-col gap-3.5 md:-mt-[84px] md:px-5">
          {!group ? (
            <EmptyState
              icon="Building"
              title="Objekt auswählen"
              description="Wähle oben in der Navigation ein Objekt für diese Ansicht aus."
            />
          ) : page.tab === 'channels' ? (
            // Neu gemountet je Objekt: Filter, Seite und Zeitleiste gehören zum Objekt.
            <ChannelCloseoutPanel
              key={group.id}
              propertyGroupId={group.id}
              canManage={page.canFull}
              today={page.today}
              onOpenApartment={(propertyId, startDate, endDate) =>
                openMonth(propertyId, [{ from: startDate, to: endDate }])
              }
              // Am Handy zeigt der Grundkalender-Reiter den Monat – er soll den Monat der Regel treffen.
              onShowInCalendar={(startDate) =>
                updateSearch({ from: startDate, tab: undefined, month: monthOf(startDate) })
              }
            />
          ) : (
            <AvailabilityCalendarCard
              calendar={calendar}
              canEdit={page.canEdit}
              canFullSync={page.canFull}
              wholeFlatOf={page.wholeFlatOf}
              onManageRules={() => updateSearch({ tab: 'channels' })}
              onOpenMonth={openMonth}
              onChanged={page.invalidate}
            />
          )}
        </div>
      </div>
    </AutomationLayerProvider>
  );
}
